import Phaser from 'phaser';
import { COLORS } from '../config/constants';
import { RESEARCH, type ResearchNodeDef } from '../data/research';
import { getResearchRankRequirement, researchManager } from '../core/ResearchManager';
import { saveManager } from '../core/SaveManager';
import { audio } from '../core/AudioSynth';

/** Category → tower art used for node icons and the detail portrait. */
export const CATEGORY_ART: Record<string, { icon: string; portrait: string; colour: number }> = {
  BALLISTICS: { icon: 'icon-kestrel', portrait: 'tower-ballistic-v3', colour: 0x6ec6ff },
  THERMAL: { icon: 'icon-pyre', portrait: 'tower-thermal-v3', colour: 0xff7a3c },
  ARTILLERY: { icon: 'icon-mordant', portrait: 'tower-artillery-v3', colour: 0xd4a24a },
  ENERGY: { icon: 'icon-helios', portrait: 'tower-energy-v3', colour: 0xb06cff },
  VULCAN: { icon: 'icon-vulcan', portrait: 'tower-vulcan-v3', colour: 0xffb347 },
  SABRE: { icon: 'icon-sabre', portrait: 'tower-sabre-v3', colour: 0xff6a5a },
  TESLA: { icon: 'icon-tesla', portrait: 'tower-tesla-v3', colour: 0x66e0ff },
  CRYO: { icon: 'icon-cryo', portrait: 'tower-cryo-v3', colour: 0x9fd8ff },
  SWARM: { icon: 'tower-dronehive-v3', portrait: 'tower-dronehive-v3', colour: 0x7dce5a },
  SYMBIOSIS: { icon: 'symbiosis-pod-missile-v1', portrait: 'symbiosis-addon-icons-v1', colour: 0x7dce5a },
  DRONE: { icon: 'icon-vulcan', portrait: 'tower-vulcan', colour: 0x7dce5a },
  'ROLLING DEATH': { icon: 'rolling-death-balls-v1', portrait: 'rolling-death-bomb-v1', colour: 0xff8b32 },
  ORBITAL: { icon: 'icon-sabre', portrait: 'tower-sabre', colour: 0xff9a5a },
  FORTIFICATION: { icon: 'icon-cryo', portrait: 'tower-cryo', colour: 0x9ad4ff },
  COMMAND: { icon: 'icon-helios', portrait: 'tower-energy', colour: 0x5ec8ff },
};

interface TreeNode {
  def: ResearchNodeDef;
  x: number;
  y: number;
  w: number;
  h: number;
  owned: boolean;
  can: boolean;
  pulse: number;
  nameText?: Phaser.GameObjects.Text;
  effText?: Phaser.GameObjects.Text;
  costText?: Phaser.GameObjects.Text;
}

interface TreeOpts {
  x: number;
  y: number;
  w: number;
  h: number;
  scrollX?: number;
  category: string;
  onUnlock: (node: ResearchNodeDef) => void;
  onExplain?: (node: ResearchNodeDef) => void;
  /** Fired when the player clicks a locked node — must explain why. */
  onLocked?: (node: ResearchNodeDef, reasons: string[]) => void;
}

const NODE_W = 176;
const NODE_MIN_H = 62;
const NODE_MAX_H = 74;
const COL_GAP = 44;
const ROW_GAP = 18;

/**
 * Interconnected research tree: tiered columns, animated flow links,
 * tower-art icons and a live detail panel. Animates via `update(time)`.
 */
export class ResearchTree {
  private scene: Phaser.Scene;
  private opts: TreeOpts;
  private container: Phaser.GameObjects.Container;
  private detailContainer: Phaser.GameObjects.Container;
  private maskShape: Phaser.GameObjects.Graphics;
  private gLinks: Phaser.GameObjects.Graphics;
  private nodes: TreeNode[] = [];
  private byId = new Map<string, TreeNode>();
  private hover: TreeNode | null = null;
  private selected: TreeNode | null = null;

  // detail panel refs
  private dPanel!: Phaser.GameObjects.Graphics;
  private dPortrait!: Phaser.GameObjects.Image;
  private dName!: Phaser.GameObjects.Text;
  private dMeta!: Phaser.GameObjects.Text;
  private dDesc!: Phaser.GameObjects.Text;
  private dReq!: Phaser.GameObjects.Text;
  private dBtn!: Phaser.GameObjects.Rectangle;
  private dBtnTxt!: Phaser.GameObjects.Text;
  private dLock!: Phaser.GameObjects.Graphics;
  private detailX = 0;
  private detailY = 0;
  private detailW = 0;
  private detailH = 0;

  constructor(scene: Phaser.Scene, opts: TreeOpts) {
    this.scene = scene;
    this.opts = opts;
    this.container = scene.add.container(0, 0).setDepth(20);
    this.detailContainer = scene.add.container(0, 0).setDepth(21);
    this.maskShape = scene.make.graphics({ x: 0, y: 0 }, false);
    this.maskShape.fillStyle(0xffffff, 1);
    this.maskShape.fillRect(opts.x, opts.y, opts.w, opts.h);
    this.container.setMask(this.maskShape.createGeometryMask());
    this.container.setX(-(opts.scrollX ?? 0));
    this.gLinks = scene.add.graphics();
    this.container.add(this.gLinks);
    this.buildNodes();
    this.buildDetail();
    this.layout();
    this.buildCards();
    this.refreshStates();
    this.updateDetail();
  }

  private art(cat: string) {
    return CATEGORY_ART[cat] ?? CATEGORY_ART.BALLISTICS;
  }

  private iconFor(def: ResearchNodeDef): string {
    if (def.category === 'SYMBIOSIS') {
      const target = def.effect.target ?? '';
      if (target.endsWith('pod_autocannon')) return 'symbiosis-pod-autocannon-v1';
      if (target.endsWith('pod_arc')) return 'symbiosis-pod-arc-v1';
      if (target.endsWith('pod_shield')) return 'symbiosis-pod-shield-v1';
      if (target.endsWith('pod_laser')) return 'symbiosis-pod-laser-v1';
      if (target.endsWith('pod_frost')) return 'symbiosis-pod-frost-v1';
      if (target.endsWith('pod_plasma')) return 'symbiosis-pod-plasma-v1';
      if (target.endsWith('pod_cluster')) return 'symbiosis-pod-cluster-v1';
      return 'symbiosis-pod-missile-v1';
    }
    if (def.category !== 'ROLLING DEATH') return this.art(def.category).icon;
    if (def.effect.target === 'rollingDeathSnow') {
      return 'rolling-death-snow-v1';
    }
    if (def.effect.type === 'cooldown') return 'rolling-death-cooldown-v1';
    return 'rolling-death-balls-v1';
  }

  private buildNodes(): void {
    const defs = researchManager.byCategory()[this.opts.category] ?? [];
    for (const def of defs) {
      const node: TreeNode = { def, x: 0, y: 0, w: NODE_W, h: this.estimateNodeHeight(def), owned: false, can: false, pulse: 0 };
      this.nodes.push(node);
      this.byId.set(def.id, node);
    }
  }

  /** Estimate a card height from the copy that will actually be rendered. */
  private estimateNodeHeight(def: ResearchNodeDef): number {
    const brief = def.desc.split(/[.!?]/)[0];
    const nameLines = Math.max(1, Math.ceil(def.name.length / 14));
    const descLines = Math.max(1, Math.ceil(Math.min(42, brief.length) / 21));
    return Phaser.Math.Clamp(26 + nameLines * 12 + descLines * 10, NODE_MIN_H, NODE_MAX_H);
  }

  /** Place tier columns, vertically centring each tier. */
  private layout(): void {
    const byTier = new Map<number, TreeNode[]>();
    for (const n of this.nodes) {
      const list = byTier.get(n.def.tier) ?? [];
      list.push(n);
      byTier.set(n.def.tier, list);
    }
    const tiers = [...byTier.keys()].sort((a, b) => a - b);
    const contentW = tiers.length * NODE_W + (tiers.length - 1) * COL_GAP;
    const startX = this.opts.x + Math.max(0, (this.opts.w - contentW) / 2);
    const centreY = this.opts.y + this.opts.h / 2;

    tiers.forEach((tier, ti) => {
      const list = byTier.get(tier)!;
      // Keep wider staged branches inside the research viewport. SYMBIOSIS has
      // six tier-two choices, so tighten only that dense column while leaving
      // the roomier branches unchanged.
      const rowGap = list.length >= 6 ? 4 : ROW_GAP;
      const totalH = list.reduce((sum, n) => sum + n.h, 0) + (list.length - 1) * rowGap;
      const startY = centreY - totalH / 2;
      list.forEach((n, ri) => {
        n.x = startX + ti * (NODE_W + COL_GAP);
        n.y = startY + list.slice(0, ri).reduce((sum, previous) => sum + previous.h + rowGap, 0);
      });
    });
  }

  private buildDetail(): void {
    const s = this.scene;
    this.detailW = 250;
    this.detailH = 380;
    this.detailX = this.opts.x + this.opts.w + 20;
    // Centre the dossier alongside the graph so it reads as a deliberate
    // third column instead of a short card pinned to the top edge.
    this.detailY = this.opts.y + 28;

    this.dPanel = s.add.graphics();
    this.detailContainer.add(this.dPanel);

    this.dPortrait = s.add.image(this.detailX + this.detailW / 2, this.detailY + 100, 'tower-ballistic');
    this.dPortrait.setDisplaySize(150, 150);
    this.detailContainer.add(this.dPortrait);

    this.dName = s.add.text(this.detailX + 14, this.detailY + 190, '', {
      fontFamily: 'Consolas, monospace',
      fontSize: '14px',
      color: '#e0e8f0',
      wordWrap: { width: this.detailW - 28 },
    });
    this.dMeta = s.add.text(this.detailX + 14, this.detailY + 214, '', {
      fontFamily: 'Consolas, monospace',
      fontSize: '10px',
      color: '#d4a24a',
    });
    this.dDesc = s.add.text(this.detailX + 14, this.detailY + 238, '', {
      fontFamily: 'Consolas, monospace',
      fontSize: '11px',
      color: '#9ad4e8',
      wordWrap: { width: this.detailW - 28 },
      lineSpacing: 3,
    });
    this.dReq = s.add.text(this.detailX + 14, this.detailY + 286, '', {
      fontFamily: 'Consolas, monospace',
      fontSize: '9px',
      color: '#6a7a88',
      wordWrap: { width: this.detailW - 28 },
      lineSpacing: 2,
    });
    this.dBtn = s.add
      .rectangle(this.detailX + this.detailW / 2, this.detailY + this.detailH - 24, this.detailW - 40, 30, 0x152028, 1)
      .setStrokeStyle(1.5, COLORS.accent)
      .setInteractive({ useHandCursor: true })
      .setVisible(false);
    this.dBtnTxt = s.add
      .text(this.detailX + this.detailW / 2, this.detailY + this.detailH - 24, '', {
        fontFamily: 'Consolas, monospace',
        fontSize: '12px',
        color: '#5ec8ff',
      })
      .setOrigin(0.5);
    this.dLock = s.add.graphics();
    this.detailContainer.add([this.dName, this.dMeta, this.dDesc, this.dReq, this.dBtn, this.dBtnTxt, this.dLock]);

    this.dBtn.on('pointerdown', () => {
      const n = this.selected ?? this.hover;
      if (!n || n.owned || !n.can) {
        audio.deny();
        return;
      }
      this.opts.onUnlock(n.def);
    });
  }

  /** Node cards are rebuilt here so the tree can be re-created per category. */
  private buildCards(): void {
    // Called once after layout; cards drawn with graphics + text for crispness.
    const s = this.scene;
    const g = s.add.graphics();
    this.container.addAt(g, 1);

    for (const n of this.nodes) {
      const { x, y, w, h, def } = n;
      const art = this.art(def.category);

      g.fillStyle(0x0d141a, 0.95);
      g.fillRoundedRect(x, y, w, h, 8);
      g.lineStyle(1.5, 0x2a3e4a, 1);
      g.strokeRoundedRect(x, y, w, h, 8);
      g.fillStyle(art.colour, 0.85);
      g.fillRect(x, y + 8, 3, h - 16);

      const iconKey = this.iconFor(def);
      if (s.textures.exists(iconKey)) {
        const icon = s.add.image(x + 34, y + h / 2, iconKey);
        icon.setDisplaySize(44, 44);
        this.container.add(icon);
      }

      const name = s.add.text(x + 62, y + 9, def.name, {
        fontFamily: 'Consolas, monospace',
        fontSize: '10px',
        color: '#c8d4e0',
        wordWrap: { width: w - 92 },
      });
      // Keep cards scannable at a glance. The detail panel carries the full
      // research description, while the card gets a compact two-line brief.
      const brief = def.desc.split(/[.!?]/)[0];
      const cardDesc = brief.length > 42 ? `${brief.slice(0, 41).replace(/\s+\S*$/, '')}…` : brief;
      const eff = s.add.text(x + 62, y + 34, cardDesc, {
        fontFamily: 'Consolas, monospace',
        fontSize: '8px',
        color: '#7a8fa0',
        wordWrap: { width: w - 72 },
        lineSpacing: 0,
      });
      const cost = s.add
        .text(x + w - 12, y + h - 7, `${def.cost}`, {
          fontFamily: 'Consolas, monospace',
          fontSize: '8px',
          color: '#d4a24a',
        })
        .setOrigin(1, 1);
      this.container.add([name, eff, cost]);
      n.nameText = name;
      n.effText = eff;
      n.costText = cost;

      const hit = s.add
        .rectangle(x + w / 2, y + h / 2, w, h, 0x000000, 0.001)
        .setInteractive({ useHandCursor: true });
      this.container.add(hit);
      hit.on('pointerover', () => {
        this.hover = n;
        this.updateDetail();
        if (!n.owned && !n.can) {
          const reasons = researchManager.lockReasons(n.def);
          this.opts.onLocked?.(n.def, reasons);
        } else {
          this.opts.onExplain?.(n.def);
        }
      });
      hit.on('pointerout', () => {
        if (this.hover === n) this.hover = null;
        this.updateDetail();
      });
      hit.on('pointerdown', () => {
        this.selected = n;
        this.updateDetail();
        if (n.owned) return;
        if (n.can) {
          this.opts.onUnlock(n.def);
          return;
        }
        const reasons = researchManager.lockReasons(n.def);
        this.opts.onLocked?.(n.def, reasons);
      });
    }
  }

  destroy(): void {
    this.container.destroy(true);
    this.detailContainer.destroy(true);
    this.maskShape.destroy();
  }

  private refreshStates(): void {
    for (const n of this.nodes) {
      n.owned = researchManager.isUnlocked(n.def.id);
      n.can = researchManager.canUnlock(n.def);
      const nameCol = n.owned ? '#7dce5a' : n.can ? '#c8d4e0' : '#5a6672';
      const effCol = n.owned ? '#9ad4e8' : n.can ? '#8aa0b0' : '#46525c';
      n.nameText?.setColor(nameCol);
      n.effText?.setColor(effCol);
      n.costText?.setColor(n.owned ? '#7dce5a' : n.can ? '#d4a24a' : researchManager.lockBadge(n.def).startsWith('RANK') ? '#ff8a7a' : '#6a5a38');
      n.costText?.setText(n.owned ? 'DONE' : n.can ? `${n.def.cost} DATA` : researchManager.lockBadge(n.def));
    }
    this.updateDetail();
  }

  /** Rebuild after a purchase so links/state reflect the new graph. */
  rebuild(): void {
    this.refreshStates();
    this.updateDetail();
  }

  private updateDetail(): void {
    const n = this.selected ?? this.hover;
    const art = this.art(n?.def.category ?? this.opts.category);
    const portrait = n?.def.category === 'SYMBIOSIS' ? this.iconFor(n.def) : art.portrait;
    if (this.scene.textures.exists(portrait)) this.dPortrait.setTexture(portrait);
    this.dPortrait.setDisplaySize(150, 150);

    this.dPanel.clear();
    this.dPanel.fillStyle(0x0b1116, 0.94);
    this.dPanel.fillRoundedRect(this.detailX, this.detailY, this.detailW, this.detailH, 8);
    this.dPanel.lineStyle(1.5, art.colour, 0.7);
    this.dPanel.strokeRoundedRect(this.detailX, this.detailY, this.detailW, this.detailH, 8);
    this.dPanel.fillStyle(art.colour, 0.9);
    this.dPanel.fillRect(this.detailX, this.detailY, this.detailW, 3);

    if (!n) {
      this.dName.setText('SELECT A NODE');
      this.dMeta.setText('');
      this.dDesc.setText('Hover or click a research node to inspect it.');
      this.dReq.setText('');
      this.dBtn.setVisible(false);
      this.dBtnTxt.setVisible(false);
      this.dLock.setVisible(false);
      return;
    }

    const { def } = n;
    this.dName.setText(def.name);
    this.dMeta.setText(`${def.category}  ·  TIER ${def.tier}  ·  ${def.cost} DATA  ·  ${def.effect.type.toUpperCase()}`);
    this.dDesc.setText(def.desc);
    const reqNames = (def.requires ?? []).map((r) => RESEARCH.find((x) => x.id === r)?.name ?? r);
    const relation = def.category === 'ORBITAL'
      ? 'APPLIES TO  ·  SUPERWEAPON COOLDOWNS / AREA'
      : def.category === 'FORTIFICATION'
        ? 'APPLIES TO  ·  BASE + BARRIER SYSTEMS'
        : def.category === 'COMMAND'
          ? 'APPLIES TO  ·  COMMAND BASE / ECONOMY / INTEGRITY'
        : 'APPLIES TO  ·  TOWER EVOLUTION / SYMBIOSIS OUTPUT';
    const rankRequirement = getResearchRankRequirement(def);
    const lockReasons = researchManager.lockReasons(def);
    const status = n.owned
      ? 'STATUS · RESEARCHED'
      : n.can
        ? `STATUS · AVAILABLE · ${def.cost} DATA`
        : `STATUS · LOCKED\n${lockReasons.map((reason) => `· ${reason}`).join('\n')}`;
    this.dReq.setText(
      `${relation}\n${status}\nUNLOCK CRITERIA\n· COMMAND RANK ${rankRequirement}${saveManager.data.rank >= rankRequirement ? ' ✓' : ` · CURRENT ${saveManager.data.rank} — TOO LOW`}\n${reqNames.length ? `· REQUIRES ${reqNames.join(', ')}` : '· NO PREREQUISITE'}\n· COST ${def.cost} DATA`,
    );

    const showBtn = !n.owned;
    // A locked node is a card state, not a disabled action button. Keep the
    // bottom action area reserved for an actionable unlock and place the lock
    // in the card's top-right status area where it cannot cover copy.
    this.dBtn.setVisible(showBtn && n.can);
    this.dBtnTxt.setVisible(showBtn && n.can);
    this.dLock.setVisible(showBtn && !n.can);
    if (showBtn) {
      this.dBtn.setStrokeStyle(1.5, n.can ? COLORS.accent : 0x2a3e4a);
      this.dBtnTxt.setText(n.can ? `UNLOCK  ${def.cost} DATA` : '');
      this.dBtnTxt.setColor(n.can ? '#5ec8ff' : '#4a5560');
      this.dLock.clear();
      if (!n.can) {
        const cx = this.detailX + this.detailW - 24;
        const cy = this.detailY + 22;
        this.dLock.setPosition(cx, cy);
        this.dLock.fillStyle(0x4a5560, 1);
        this.dLock.fillRoundedRect(-9, -1, 18, 13, 2);
        this.dLock.lineStyle(2, 0x4a5560, 1);
        this.dLock.beginPath();
        this.dLock.arc(0, -2, 7, Math.PI, 0, false);
        this.dLock.strokePath();
        this.dLock.fillStyle(0x10161c, 1);
        this.dLock.fillCircle(0, 5, 2);
        this.dLock.lineStyle(1.5, 0x10161c, 1);
        this.dLock.lineBetween(0, 5, 0, 8);
      }
    }
  }

  /** Animated pass: flowing links + availability pulse. */
  update(time: number): void {
    const g = this.gLinks;
    g.clear();

    for (const n of this.nodes) {
      if (!n.def.requires?.length) continue;
      for (const reqId of n.def.requires) {
        const src = this.byId.get(reqId);
        if (!src) continue;
        const x1 = src.x + src.w;
        const y1 = src.y + src.h / 2;
        const x2 = n.x;
        const y2 = n.y + n.h / 2;
        const mid = (x1 + x2) / 2;

        const owned = src.owned && n.owned;
        const ready = src.owned && !n.owned && n.can;
        const base = owned ? COLORS.good : ready ? COLORS.accent : 0x2a3e4a;
        const alpha = owned ? 0.85 : ready ? 0.7 : 0.4;

        g.lineStyle(owned ? 2.5 : 1.5, base, alpha);
        g.beginPath();
        g.moveTo(x1, y1);
        g.lineTo(mid, y1);
        g.lineTo(mid, y2);
        g.lineTo(x2, y2);
        g.strokePath();

        // Arrow head
        g.fillStyle(base, alpha);
        g.fillTriangle(x2, y2, x2 - 7, y2 - 4, x2 - 7, y2 + 4);

        // Animated energy flow on ready links.
        if (ready) {
          const t = (time / 900) % 1;
          for (let k = 0; k < 3; k++) {
            const f = (t + k / 3) % 1;
            const px = x1 + (mid - x1) * Math.min(1, f * 3);
            const py = f * 3 < 1 ? y1 : f * 3 < 2 ? y1 + (y2 - y1) * (f * 3 - 1) : y2;
            g.fillStyle(0xffffff, 0.75 * (1 - Math.abs(0.5 - f) * 1.2));
            g.fillCircle(px, py, 2.2);
          }
        }
      }
    }

    // Node pulse for available nodes.
    for (const n of this.nodes) {
      const target = n.can && !n.owned ? 0.5 + 0.5 * Math.sin(time / 320 + n.x) : 0;
      n.pulse += (target - n.pulse) * 0.15;
      const glow = n.owned ? 0x7dce5a : n.can ? COLORS.accent : 0x2a3e4a;
      g.lineStyle(1.5 + n.pulse * 1.6, glow, n.owned ? 0.9 : 0.35 + n.pulse * 0.55);
      g.strokeRoundedRect(n.x, n.y, n.w, n.h, 8);
      if (n.pulse > 0.05) {
        g.lineStyle(1, glow, n.pulse * 0.18);
        g.strokeRoundedRect(n.x - 3, n.y - 3, n.w + 6, n.h + 6, 10);
      }
    }
  }
}
